import { BidiModule, DIR_DOCUMENT } from '@angular/cdk/bidi';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LayoutModule } from '@layout/layout.module';
import { JwtModule, JwtModuleOptions, JwtInterceptor } from '@auth0/angular-jwt';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '@env/environment';
import { AppRoutingModule } from './app-routing.module';
import { FilterModule } from '@features/filter/filter.module';
import { AppComponent } from './app.component/app.component';
import { StorageConstant } from '@shared/constants/storage.constant';
import { TokenInterceptor } from '@core/interceptors/token.interceptor';
import { LocaleIdHandlerService } from '@shared/services/locale-id-handler.service';
import { SharedModule } from '@shared/shared.module';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import ar from '@angular/common/locales/ar';
import { FormsModule } from '@angular/forms';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { MessagingService } from '@core/services/messaging.service';
import { DeliveryNotesModule } from '@features/delivery-notes/delivery-notes.module';
import { CoreModule } from '@core/core.module';
import { ApiConstant } from '@shared/constants/api.constant';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { ConfigConstant } from '@shared/constants';
import {
  RecaptchaV3Module,
  RECAPTCHA_V3_SITE_KEY,
  RECAPTCHA_SETTINGS,
  RecaptchaSettings,
} from 'ng-recaptcha';
import { ErrorHandlerInterceptor } from '@core/interceptors/error-handler.interceptor';

registerLocaleData(en, ar);

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/locales/', '.json');
}

export function getToken(): string {
  const token = localStorage.getItem(StorageConstant.AUTH_USER);
  if (token) {
    return JSON.parse(token)?.accessToken;
  }
  return null;
}

const JWT_Module_Options: JwtModuleOptions = {
  config: {
    tokenGetter: getToken,
    allowedDomains: ConfigConstant.APP_WHITE_LISTED_DOMAINS,
    disallowedRoutes: [ApiConstant.REFRESH_TOKEN],
    headerName: 'Authorization',
    authScheme: 'Bearer ',
  },
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    HttpClientModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BidiModule,
    CoreModule,
    JwtModule.forRoot(JWT_Module_Options),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    SharedModule,
    LayoutModule,
    FilterModule,
    FormsModule,
    AngularFireMessagingModule,
    DeliveryNotesModule,
    RecaptchaV3Module,
    AngularFireModule.initializeApp(environment.firebase),
    ...(environment.google_analytics
      ? [
          NgxGoogleAnalyticsModule.forRoot(environment.google_analytics),
          NgxGoogleAnalyticsRouterModule.forRoot(),
        ]
      : []),
  ],
  providers: [
    TranslateService,
    { provide: DIR_DOCUMENT, useValue: 'rtl' },
    { provide: LOCALE_ID, useClass: LocaleIdHandlerService, deps: [TranslateService] },

    // ================= Interceptors =================
    JwtInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    MessagingService,
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaV3SiteKey },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: environment.recaptchaV2SiteKey } as RecaptchaSettings,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
